import React from "react";
import {
  Button,
  createStyles,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  ListItem,
  ListItemIcon,
  ListItemText,
  makeStyles,
  Paper,
  Typography
} from "@material-ui/core";
import { FileCopy, OpenInNew } from "@material-ui/icons";
import { ArrowLeftRight } from "lucide-react";
import AnnouncementIcon from "react-icons/lib/md/announcement";
import { useDialog, useGlobalStore, useTenant } from "@hooks";
import { ThemeType, mixins } from "@theme";
import locale from "@bonliva-crm/locale";
import bonlivaLogo from "../theme/bonliva-logo.svg";
import bonlivaO from "../../assets/bonliva-o.png";
import { Small } from "./typography/Small";
import { PlainLink } from "./typography/PlainLink";
import clipboard from "@utils/clipboard";
import { environment } from "../../environments/environment";
import { useDbName } from "../context/db-name-context";
import { UserBar } from "../containers/UserBar/UserBar";
import { ApiTokensDialog } from "../containers/ApiTokens/ApiTokensDialog";

type Props = {
  mainMenu?: React.ReactNode;
  onBurgerClick?: (e: React.SyntheticEvent<any>) => any;
  onLoginAsClick?: (e: React.SyntheticEvent<any>) => any;
  onLogoClick?: () => void;
  onLogout?: () => void;
  showLoginAs?: boolean;
};

/**
 * Displays the left drawer.
 */

export function AppDrawer(props: Props) {
  const classes = useStyles({});
  const store = useGlobalStore();
  const { tenant, setTenant } = useTenant();
  const dbName = useDbName();
  const dialog = useDialog<null>();
  const [apiTokensOpen, setApiTokensOpen] = React.useState(false);

  return (
    <Paper className={classes.root} elevation={0}>
      <div className={classes.topbar}>
        <div className={classes.burgerLogoWrapper}>
          {tenant === "bonliva-ab" && (
            <>
              <img
                src={bonlivaLogo}
                alt="Bonliva"
                className="sidebar-logo-full"
                onClick={props.onLogoClick}
                style={{ height: 16, width: "auto", cursor: "pointer" }}
              />
              <img
                src={bonlivaO}
                alt="Bonliva"
                className="sidebar-logo-collapsed"
                onClick={props.onLogoClick}
                style={{ width: 24, height: 24, cursor: "pointer" }}
              />
            </>
          )}

          {tenant === "wcc" && (
            <>
              <Typography variant="h6" color="primary" className="sidebar-logo-full">
                We Connect Care
              </Typography>
              <Typography variant="h6" color="primary" className="sidebar-logo-collapsed">
                W
              </Typography>
            </>
          )}

          {store.user.can("tenant-wcc:read:all") && (
            <IconButton
              size="small"
              onClick={() => setTenant(tenant === "bonliva-ab" ? "wcc" : "bonliva-ab")}
              style={{ marginLeft: "auto" }}
            >
              <ArrowLeftRight size={20} />
            </IconButton>
          )}
        </div>

      </div>

      {props.mainMenu}

      <div className={classes.bottomBar}>
        <Divider classes={{ root: classes.divider }} />

        <UserBar
          onLogout={props.onLogout}
          onLoginAs={props.showLoginAs ? props.onLoginAsClick : undefined}
          onApiTokens={() => setApiTokensOpen(true)}
        />

        <ListItem
          onClick={dialog.onOpen}
          className={classes.listItem}
          data-test="link"
          button
        >
          <ListItemIcon style={{ minWidth: 40 }}>
            <AnnouncementIcon />
          </ListItemIcon>
          <ListItemText
            primary={locale.FeedbackAndReport}
            className={classes.listItemText}
          />
        </ListItem>

        <div className={classes.revisionBar}>
          <div className="sidebar-logo-collapsed" style={{ width: 48, textAlign: "center", marginLeft: -8 }}>
            <Small display="block">
              {environment.version}
            </Small>
          </div>
          <div className="sidebar-logo-full">
            <Small
              id="app__revision"
              onClick={copyRevisionToClipboard}
              title="Copy to clipboard"
              display="block"
            >
              ver. {environment.version} ({environment.build}){" "}
              <FileCopy style={{ fontSize: 10 }} />
            </Small>

            {environment.branchName && !environment.branchName.includes("master") && (
              <Small display="block">branch:&nbsp;{environment.branchName}</Small>
            )}

            {dbName && !environment.branchName.includes("master") && (
              <Small display="block">db: {dbName}</Small>
            )}

            {store.canUpdate && (
              <PlainLink
                onClick={() => window.location.reload()}
                style={{ display: "block", fontSize: 10 }}
              >
                Update
              </PlainLink>
            )}
          </div>
        </div>
      </div>

      <Dialog open={dialog.open} onClose={dialog.onClose} maxWidth="sm" fullWidth>
        <DialogTitle>{locale.FeedbackAndReport}</DialogTitle>
        <DialogContent className={classes.linkWrap}>
          <Link
            href="https://forms.office.com/Pages/ResponsePage.aspx?id=OGRa6DOLKUq4fpqu_-y7-Tb7mjuPlmlMt5LTlT0zL3xURFNRMDcyTFZUMEMwT1JLMVFKOTZYQzMxTi4u"
            target="_blank"
            className={classes.feedbackLink}
          >
            Rapportera bug <OpenInNew />
          </Link>
          <Link
            href="https://forms.office.com/Pages/ResponsePage.aspx?id=OGRa6DOLKUq4fpqu_-y7-Tb7mjuPlmlMt5LTlT0zL3xUOEs5SlZPSkRaOUNOMUZOME0yOFQ4WVFLMi4u"
            target="_blank"
            className={classes.feedbackLink}
          >
            Skicka in idé/problem <OpenInNew />
          </Link>
          <Link
            href="https://forms.office.com/Pages/ResponsePage.aspx?id=OGRa6DOLKUq4fpqu_-y7-Tb7mjuPlmlMt5LTlT0zL3xUMkxLMUdUNVJSWlkzM01UNk9JWkRZSzU5Ni4u"
            target="_blank"
            className={classes.feedbackLink}
          >
            Beställa rapport <OpenInNew />
          </Link>
          <Link
            href="https://docconnectnordicab.sharepoint.com/:l:/g/FDE1zw3KgddBr24feqvGmccB4sBsLoe5aFQd8w6TUWNeEA?nav=MmEzYmY2YmItYzgxYS00OGY4LTk3OTMtNTRmOTA3M2E3MmZm"
            target="_blank"
            className={classes.feedbackLink}
          >
            Beställning av ny funktionalitet <OpenInNew />
          </Link>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={dialog.onClose}>
            {locale.Cancel}
          </Button>
        </DialogActions>
      </Dialog>
      <ApiTokensDialog
        open={apiTokensOpen}
        onClose={() => setApiTokensOpen(false)}
      />
    </Paper>
  );
}

const useStyles = makeStyles((theme: ThemeType) =>
  createStyles({
    ...mixins.withListItem(theme),

    root: {
      display: "flex",
      flexDirection: "column",
      minHeight: "100%",
      position: "static",
      width: 250,
      overflow: "hidden",
      background: "transparent",
      "&:hover": {
        overflowY: "auto"
      }
    },

    topbar: {
      alignItems: "flex-start",
      display: "flex",
      flexDirection: "column",
      flexShrink: 0
    },

    burgerLogoWrapper: {
      width: "100%",
      display: "flex",
      alignItems: "center",
      padding: 16
    },

    burger: {
      width: "auto",
      height: 24,
      marginRight: 20,
      padding: 0
    },

    divider: {
      width: "100%"
    },

    userbarClickable: {
      cursor: "pointer",
      width: "100%",
      "&:hover": {
        backgroundColor: "rgba(0, 0, 0, 0.04)"
      }
    },

    bottomBar: {
      marginTop: "auto",
      backgroundColor: theme.palette.background.default
    },

    updLink: {
      cursor: "pointer"
    },

    linkWrap: {
      display: "flex",
      flexDirection: "column" as const,
      gap: 5
    },

    feedbackLink: {
      display: "flex",
      alignItems: "center"
    },

    revisionBar: {
      padding: theme.spacing(),
      textAlign: "center",

      "& > div > span": {
        cursor: "pointer",
        transition: theme.transitions.create("color")
      },

      "& > div > span:hover": {
        color: theme.palette.primary.main
      }
    }
  })
);

function copyRevisionToClipboard() {
  clipboard(`rev. ${process.env.CRM_APP_REVISION}`);
}
